import { useState } from 'react';
import { Link } from 'react-router-dom';
import JaLayout from '@/components/feature/JaLayout';
import useSEO from '@/hooks/useSEO';

// ── Service sub-nav cards ──
const serviceCards = [
  { icon: 'ri-fire-line', title: '人気処方', desc: '300種以上の実績ある処方をブランドに合わせてご提供。完全カスタム処方の開発も可能です。', path: '/ja/services/hot-formulas' },
  { icon: 'ri-leaf-line', title: '天然原料', desc: '完全なトレーサビリティと品質証明を持つ上質な天然原料を調達。安心できる素材選びをサポートします。', path: '/ja/services/natural-ingredients' },
  { icon: 'ri-capsule-line', title: '剤型', desc: 'カプセル・錠剤・ソフトジェル・粉末・グミ・飲料など、ブランドに合った剤型で製造します。', path: '/ja/services/dosage-forms' },
  { icon: 'ri-box-3-line', title: 'パッケージデザイン', desc: '容器選定からラベルコンプライアンス審査まで、パッケージング全体をサポートします。', path: '/ja/services/packaging' },
];

// ── Product gallery ──
const galleryItems = [
  { name: 'マルチビタミン', img: 'https://readdy.ai/api/search-image?query=multivitamin%20supplement%20colorful%20capsules%20tablets%20spilling%20from%20orange%20bottle%20on%20white%20background%2C%20health%20supplement%20product%20photography&width=300&height=300&seq=gallery-01&orientation=squarish' },
  { name: 'プロバイオティクス', img: 'https://readdy.ai/api/search-image?query=probiotic%20supplement%20white%20powder%20sachet%20packet%20with%20green%20healthy%20ingredients%2C%20natural%20health%20food%20product%20photography%20on%20light%20background&width=300&height=300&seq=gallery-02&orientation=squarish' },
  { name: 'コラーゲンドリンク', img: 'https://readdy.ai/api/search-image?query=collagen%20drink%20bottle%20beauty%20supplement%20beverage%20with%20pink%20flowers%20and%20cream%20background%2C%20elegant%20health%20drink%20product%20photography&width=300&height=300&seq=gallery-03&orientation=squarish' },
  { name: 'ウコンカプセル', img: 'https://readdy.ai/api/search-image?query=turmeric%20curcumin%20supplement%20capsules%20with%20fresh%20turmeric%20root%20on%20wooden%20surface%2C%20warm%20golden%20orange%20natural%20health%20product%20photography&width=300&height=300&seq=gallery-04&orientation=squarish' },
  { name: 'フィッシュオイル', img: 'https://readdy.ai/api/search-image?query=omega3%20fish%20oil%20softgel%20capsules%20golden%20yellow%20scattered%20on%20white%20background%2C%20health%20supplement%20close%20up%20product%20photography&width=300&height=300&seq=gallery-05&orientation=squarish' },
  { name: 'ハーブエキス錠', img: 'https://readdy.ai/api/search-image?query=herbal%20plant%20extract%20green%20tablets%20supplements%20with%20fresh%20botanical%20leaves%20and%20herbs%20on%20white%20marble%20background%2C%20natural%20health%20product%20photography&width=300&height=300&seq=gallery-06&orientation=squarish' },
  { name: '美容複合処方', img: 'https://readdy.ai/api/search-image?query=beauty%20supplement%20pink%20capsules%20with%20rose%20petals%20and%20natural%20ingredients%20on%20pastel%20background%2C%20elegant%20feminine%20health%20product%20photography&width=300&height=300&seq=gallery-07&orientation=squarish' },
  { name: '機能性飲料', img: 'https://readdy.ai/api/search-image?query=functional%20health%20drink%20small%20bottle%20energy%20shot%20supplement%20with%20fruits%20vegetables%20on%20clean%20white%20background%2C%20health%20beverage%20product%20photography&width=300&height=300&seq=gallery-08&orientation=squarish' },
  { name: 'ペット用サプリ', img: 'https://readdy.ai/api/search-image?query=pet%20health%20supplement%20treats%20vitamins%20for%20dog%20cat%20on%20light%20background%2C%20cute%20pet%20care%20product%20photography%2C%20natural%20ingredients%20visible&width=300&height=300&seq=gallery-09&orientation=squarish' },
  { name: 'ハーブティーバッグ', img: 'https://readdy.ai/api/search-image?query=herbal%20tea%20bags%20with%20dried%20flowers%20chamomile%20lavender%20natural%20herbs%20on%20white%20background%2C%20health%20wellness%20tea%20product%20photography&width=300&height=300&seq=gallery-10&orientation=squarish' },
];

// ── Dosage forms ──
const dosageCategories = ['すべて', '固形剤型', '液体剤型', '粉末剤型', '特殊剤型'];

const dosageForms = [
  { category: '固形剤型', name: 'ハードカプセル', icon: 'ri-capsule-line', desc: '植物性またはゼラチンカプセルに粉末・顆粒・微粒子を充填。飲みやすく吸収効果も優れています。', tags: ['飲みやすい', '複数サイズ', '植物性対応'], img: 'https://readdy.ai/api/search-image?query=hard%20capsule%20supplement%20green%20white%20transparent%20capsules%20close%20up%20on%20white%20background%2C%20pharmaceutical%20product%20photography&width=400&height=260&seq=dosage-cap-01&orientation=landscape' },
  { category: '固形剤型', name: 'ソフトジェルカプセル', icon: 'ri-medicine-bottle-line', desc: 'フィッシュオイルや月見草オイルなどの油脂系成分に最適。丸みのあるフォルムで飲みやすく、安定性が高い。', tags: ['油脂系対応', '飲みやすい', '安定性高'], img: 'https://readdy.ai/api/search-image?query=soft%20gel%20capsules%20omega3%20fish%20oil%20golden%20yellow%20softgels%20scattered%20on%20white%20clean%20background%2C%20supplement%20product%20photography&width=400&height=260&seq=dosage-soft-01&orientation=landscape' },
  { category: '固形剤型', name: '錠剤', icon: 'ri-medicine-bottle-line', desc: '様々な形状と色に圧縮成形。大量生産に適しており、競争力のあるコスト構造を実現します。', tags: ['大量生産', 'コスト優位', '形状選択可'], img: 'https://readdy.ai/api/search-image?query=supplement%20tablets%20pills%20round%20white%20green%20colorful%20on%20white%20background%2C%20pharmaceutical%20health%20product%20photography%20closeup&width=400&height=260&seq=dosage-tab-01&orientation=landscape' },
  { category: '固形剤型', name: 'チュアブル錠', icon: 'ri-heart-pulse-line', desc: '調味料を加えた美味しい味。お子様や錠剤を飲み込みにくい方に最適です。', tags: ['美味しい', 'お子様向け', 'フレーバー豊富'], img: 'https://readdy.ai/api/search-image?query=chewable%20tablets%20colorful%20fruit%20flavored%20health%20supplement%20vitamins%20for%20children%20on%20light%20background%2C%20cute%20product%20photography&width=400&height=260&seq=dosage-chew-01&orientation=landscape' },
  { category: '液体剤型', name: '機能性飲料', icon: 'ri-cup-line', desc: '液体剤型で素早く吸収。果汁や香料でフレーバリングでき、手軽さを重視する現代の消費者に人気です。', tags: ['素早い吸収', '美味しい', 'ポータブル'], img: 'https://readdy.ai/api/search-image?query=functional%20health%20drink%20shot%20bottle%20small%20beverage%20supplement%20with%20colorful%20fruits%20on%20white%20background%2C%20product%20photography&width=400&height=260&seq=dosage-drink-01&orientation=landscape' },
  { category: '液体剤型', name: '経口液', icon: 'ri-drop-line', desc: 'ドロップまたは経口液の形態。精密な用量管理が可能で、乳幼児・高齢者などの特定層に最適。', tags: ['精密な用量', '特定層向け', '吸収しやすい'], img: 'https://readdy.ai/api/search-image?query=oral%20liquid%20supplement%20dropper%20bottle%20health%20tincture%20on%20clean%20white%20background%2C%20medical%20health%20product%20photography&width=400&height=260&seq=dosage-oral-01&orientation=landscape' },
  { category: '粉末剤型', name: '粉末スティック', icon: 'ri-file-paper-line', desc: '便利な個包装。持ち運びやすく、水に溶かすか直接摂取可能。プロテインやプロバイオティクス製品に最適。', tags: ['持ち運びやすい', '水溶性', '個包装'], img: 'https://readdy.ai/api/search-image?query=powder%20sachet%20supplement%20protein%20probiotic%20white%20packet%20with%20spoon%20on%20clean%20white%20background%2C%20health%20food%20product%20photography&width=400&height=260&seq=dosage-pow-01&orientation=landscape' },
  { category: '粉末剤型', name: '顆粒剤', icon: 'ri-seedling-line', desc: '粉末より口当たりが良い顆粒形態。溶解性に優れ、ビタミンやプロバイオティクス製品に最適。', tags: ['口当たり良い', '溶けやすい', '飲みやすい'], img: 'https://readdy.ai/api/search-image?query=granule%20supplement%20health%20food%20small%20pellets%20granular%20form%20on%20white%20background%2C%20pharmaceutical%20product%20photography%20closeup&width=400&height=260&seq=dosage-gran-01&orientation=landscape' },
  { category: '特殊剤型', name: 'グミ', icon: 'ri-star-smile-line', desc: 'グミキャンディー形態で食べやすく、お子様や若い世代に人気。急成長中の市場セグメントです。', tags: ['食べやすい', '若年層向け', '売れ筋'], img: 'https://readdy.ai/api/search-image?query=gummy%20bear%20vitamin%20supplement%20colorful%20fruit%20flavored%20gummies%20on%20white%20background%2C%20health%20supplement%20candy%20product%20photography&width=400&height=260&seq=dosage-gummy-01&orientation=landscape' },
];

// ── Image-text blocks ──
const imageTextBlocks = [
  {
    tag: '原料調達',
    title: '厳選天然原料 — 品質保証',
    desc: '康永は国内外のトップクラス原料サプライヤーと長期的なパートナーシップを維持しています。すべての入荷バッチは農薬残留・重金属・微生物など厳格な受入検査を経て、コンプライアンスを確保します。',
    points: [
      '認定原料サプライヤーとの長期提携',
      'バッチごとに合格検査報告書を添付',
      'サプライヤーの工程と品質を定期監査',
      '原料産地と成分純度のトレーサビリティ',
    ],
    img: 'https://readdy.ai/api/search-image?query=natural%20herb%20botanical%20raw%20ingredients%20turmeric%20ginger%20green%20tea%20extract%20dried%20herbs%20spices%20arranged%20on%20white%20wooden%20surface%2C%20health%20supplement%20raw%20materials%20professional%20photography&width=720&height=500&seq=service-raw-01&orientation=landscape',
    imgLeft: false,
  },
  {
    tag: '製造',
    title: 'GMP認定工場・多剤型製造能力',
    desc: '康永の工場はGMP規格に準拠し、カプセル・錠剤・粉末・液体剤型の完全な製造ラインを備えています。少量試作から大量量産まで柔軟に対応し、バッチごとに安定した品質を確保します。',
    points: [
      'GMP準拠のクリーン製造環境',
      'カプセル・錠剤・スティック・飲料の並行製造ライン',
      '少量試作（3,000個から）から大量量産まで対応',
      '全工程の温湿度モニタリング記録',
    ],
    img: 'https://readdy.ai/api/search-image?query=GMP%20certified%20health%20supplement%20production%20line%20workers%20wearing%20white%20uniform%20hairnet%20operating%20manufacturing%20equipment%20in%20clean%20room%2C%20professional%20pharmaceutical%20factory%20interior&width=720&height=500&seq=service-prod-01&orientation=landscape',
    imgLeft: true,
  },
];

// ── Compare table ──
const compareRows = [
  { feature: '処方の出所', odm: '康永R&Dが開発・提供', oem: 'お客様が持込' },
  { feature: 'R&Dサポート', odm: '完全な研究開発サポート', oem: '必要に応じてコンサルティング' },
  { feature: '最小発注量', odm: '少量（柔軟対応）', oem: '処方の複雑さによる' },
  { feature: '上市スピード', odm: '比較的早い', oem: '処方審査スケジュールによる' },
  { feature: '処方の機密性', odm: '標準NDA契約', oem: '厳格なNDA契約' },
  { feature: 'パッケージデザイン', odm: 'デザインサポートあり', oem: 'お客様要件に準ずる' },
  { feature: '法規申請', odm: 'サポート可能', oem: 'サポート可能' },
  { feature: '最適な対象', odm: '新ブランド、迅速な上市', oem: '成熟ブランド、自社処方保有' },
];

// ── Certifications & QC ──
const certifications = [
  { name: 'GMP認定', desc: '優良製造規範', icon: 'ri-award-line' },
  { name: 'ISO 22000', desc: '食品安全マネジメントシステム', icon: 'ri-shield-check-line' },
  { name: 'HACCP', desc: '危害分析重要管理点', icon: 'ri-scan-line' },
  { name: 'SGS検査', desc: '第三者安全認証', icon: 'ri-verified-badge-line' },
];

const qcSteps = [
  { icon: 'ri-truck-line', title: '入荷検収', desc: 'バッチごとに外観・成分含量・微生物・重金属など多項目の検査を実施。' },
  { icon: 'ri-settings-3-line', title: '製造工程管理', desc: '全製造工程で重量・温湿度・崩壊度などの重要品質指標をモニタリング。' },
  { icon: 'ri-test-tube-line', title: '完成品全項目検査', desc: '出荷前に微生物・農薬残留・重金属など完全な安全性スクリーニングを実施。' },
  { icon: 'ri-file-check-line', title: 'CoA発行', desc: 'バッチごとに完全な品質証明書（CoA）を発行。お客様の記録・市場監査に対応。' },
];

// ── Process steps ──
const processSteps = [
  { num: '01', icon: 'ri-discuss-line', title: 'ご相談', desc: '製品要件・ターゲット層・予算をお伝えください。担当者が初期提案を行います。' },
  { num: '02', icon: 'ri-flask-line', title: '処方設計', desc: '研究開発チームが処方案を提案し、成分と効能をご説明します。' },
  { num: '03', icon: 'ri-test-tube-line', title: '試作サンプル', desc: '少量試作で品質・味・外観を確認していただけます。' },
  { num: '04', icon: 'ri-layout-line', title: 'パッケージデザイン', desc: 'パッケージ仕様・ラベル内容・デザインを確認し、法規準拠を確保します。' },
  { num: '05', icon: 'ri-building-line', title: '量産開始', desc: '契約締結後、GMP品質管理のもと量産を開始します。' },
  { num: '06', icon: 'ri-truck-line', title: '納品', desc: '品質検収後、約束のスケジュールで出荷。完全なバッチ検査報告書を添付。' },
];

// ── FAQ ──
const faqs = [
  { q: '最小発注量（MOQ）はどのくらいですか？', a: 'MOQは剤型によって異なります。カプセル・錠剤は通常3,000〜5,000本から、粉末スティック・飲料は約5,000個からとなります。詳細は営業担当者にお問い合わせください。' },
  { q: 'どの剤型を製造できますか？', a: '康永はハードカプセル・ソフトジェルカプセル・錠剤・チュアブル錠・粉末スティック・顆粒剤・機能性飲料・経口液・グミを製造できます。製品に最適な剤型を一緒に選定します。' },
  { q: 'アイデアだけで処方がない場合でも依頼できますか？', a: 'もちろんです！ODMモデルでは、ブランドの方向性と目標とする健康効果をお伝えいただければ、研究開発チームがカスタム処方を設計します。技術的な背景知識は一切不要です。' },
  { q: '台湾の健康食品規制に準拠していますか？', a: 'はい。康永は健康食品に関する法規に精通しており、ラベルコンプライアンス審査・健康食品認定申請・法規申請をサポートし、適法な上市を実現します。' },
  { q: 'サンプルから量産までどのくらいかかりますか？', a: 'サンプル製造は通常2〜4週間。確認後の量産は製品の複雑さと発注規模によって約4〜8週間かかります。相談時にスケジュールを確認します。' },
  { q: 'パッケージングとラベルデザインのサービスはありますか？', a: 'はい。パッケージデザインの提案とラベルコンプライアンス審査を提供します。お客様の設計データに合わせた製造も可能です。' },
];

export default function JaServicesPage() {
  const [activeDosage, setActiveDosage] = useState('すべて');
  const [openFaqIdx, setOpenFaqIdx] = useState<number | null>(null);

  useSEO({
    title: 'OEM/ODMサービス｜カプセル・錢剤・粉末・飲料｜康永生技',
    description: '康永生技は健康食品OEM/ODMサービスを全面的に提供。カプセル・ソフトジェル・錢剤・粉末スティック・グミ・機能性飲料など。GMP認定、少ロット対応。',
    keywords: '健康食品OEMサービス, カプセル製造 台湾, 錢剤OEM, 粉末スティックODM, グミサプリメントメーカー',
    lang: 'ja',
    canonical: 'https://kangyong.com/ja/services',
  });

  const filteredDosage = activeDosage === 'すべて' ? dosageForms : dosageForms.filter((d) => d.category === activeDosage);

  return (
    <JaLayout>
      {/* ── Hero ── */}
      <section className="relative pt-20 min-h-[440px] md:min-h-[500px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=colorful%20variety%20of%20health%20supplement%20products%20capsules%20tablets%20powder%20drinks%20arranged%20on%20clean%20white%20surface%2C%20vibrant%20health%20food%20products%20display%2C%20professional%20commercial%20photography%2C%20fresh%20natural%20ingredients%20visible&width=1920&height=500&seq=services-hero-bg-01&orientation=landscape"
            alt="OEM/ODMサービス"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a1a]/90 via-[#1a3a1a]/65 to-[#1a3a1a]/20" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-16 pt-28">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 tracking-wide">
              OEM/ODMサービス
            </span>
            <h1 className="text-white font-bold leading-tight mb-5">
              <span className="block">多剤型OEM製造</span>
              <span className="block text-green-300">ワンストップ健康食品OEM/ODMサービス</span>
            </h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl mb-8">
              康永はカプセル・錠剤・粉末・飲料など多種多様な剤型を提供。処方開発からパッケージング・納品まで、GMP認定品質でお客様のブランドをサポートします。
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/ja/contact/form" className="whitespace-nowrap bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[1rem] px-7 py-3.5 rounded-full transition-all duration-300 cursor-pointer">
                今すぐ相談する
              </Link>
              <Link to="/ja/partnership" className="whitespace-nowrap bg-white/15 hover:bg-white/25 border border-white/60 text-white font-semibold text-[1rem] px-7 py-3.5 rounded-full transition-all duration-300 cursor-pointer">
                フローを見る
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Service Cards ── */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">サービスラインナップ</span>
            <h2 className="text-[#1a2e1a] font-bold text-[2rem] md:text-[2.5rem] mb-4">コアサービス</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              新ブランドの立上げから既存製品ラインの拡充まで、康永が包括的な製造サポートを提供します。
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {serviceCards.map((s) => (
              <Link key={s.path} to={s.path} className="flex items-start gap-5 bg-[#f7faf7] rounded-2xl p-7 border border-gray-100 hover:border-green-200 hover:-translate-y-1 transition-all duration-300 cursor-pointer group">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl border border-green-100 flex-shrink-0 group-hover:bg-[#4CAF50] transition-colors duration-300">
                  <i className={`${s.icon} text-[#4CAF50] text-xl group-hover:text-white transition-colors duration-300`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{s.title}</h3>
                  <p className="text-gray-500 text-[0.9375rem] leading-[1.7] mb-3">{s.desc}</p>
                  <span className="inline-flex items-center gap-1 text-[#4CAF50] text-[0.875rem] font-medium">詳しく見る <i className="ri-arrow-right-line text-xs" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Product Gallery ── */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">製品例</span>
            <h2 className="text-[#1a2e1a] font-bold text-[2rem] md:text-[2.5rem] mb-4">多様な製品タイプ、あらゆるニーズに対応</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              康永の受託製造は健康・美容・スポーツ・ペットなど多分野をカバー。以下はサンプル製品の一部です
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {galleryItems.map((item) => (
              <div key={item.name} className="group cursor-default">
                <div className="rounded-2xl overflow-hidden border border-gray-100 hover:border-green-200 transition-all duration-300 hover:-translate-y-1">
                  <div className="w-full h-[200px] sm:h-[220px] overflow-hidden">
                    <img src={item.img} alt={item.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  <div className="px-3 py-2.5 text-center bg-[#f7faf7]">
                    <p className="text-[#1a2e1a] font-semibold text-[0.875rem]">{item.name}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Dosage Forms ── */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">剤型</span>
            <h2 className="text-[#1a2e1a] font-bold text-[2rem] md:text-[2.5rem] mb-4">あらゆるニーズに応える多様な剤型</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              康永は完全な多剤型製造設備を有しています。製品のポジショニングとターゲット層に最適な剤型をお選びください
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {dosageCategories.map((cat) => (
              <button key={cat} onClick={() => setActiveDosage(cat)} className={`whitespace-nowrap px-5 py-2 rounded-full text-[0.9375rem] font-medium transition-all duration-200 cursor-pointer ${activeDosage === cat ? 'bg-[#4CAF50] text-white' : 'bg-white text-gray-500 border border-gray-200 hover:border-green-300 hover:text-[#4CAF50]'}`}>
                {cat}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDosage.map((item) => (
              <div key={item.name} className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-green-200 hover:-translate-y-1 transition-all duration-300">
                <div className="w-full h-[220px] overflow-hidden">
                  <img src={item.img} alt={item.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-6">
                  <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.75rem] font-medium px-3 py-1 rounded-full mb-3 border border-green-100">{item.category}</span>
                  <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{item.name}</h3>
                  <p className="text-gray-500 text-[0.875rem] leading-[1.75] mb-4">{item.desc}</p>
                  <div className="flex flex-wrap gap-2">
                    {item.tags.map((tag) => (
                      <span key={tag} className="bg-[#f7faf7] border border-gray-200 text-gray-500 text-[0.75rem] px-3 py-1 rounded-full">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Image-Text Blocks ── */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">サービス詳細</span>
            <h2 className="text-[#1a2e1a] font-bold text-[2rem] md:text-[2.5rem] mb-4">原料から完成品まで — トータル品質管理</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              康永は垂直統合型のOEMサービスを提供。専門チームがすべての工程を管理します
            </p>
          </div>
          <div className="space-y-16">
            {imageTextBlocks.map((block) => (
              <div key={block.title} className={`flex flex-col ${block.imgLeft ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-10 lg:gap-16 items-center`}>
                <div className="w-full lg:w-1/2">
                  <span className="inline-block bg-green-100 text-[#4CAF50] text-[0.8125rem] font-medium px-3 py-1 rounded-full mb-4">{block.tag}</span>
                  <h3 className="text-[#1a2e1a] font-bold text-[1.625rem] leading-tight mb-4">{block.title}</h3>
                  <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-6">{block.desc}</p>
                  <ul className="space-y-3">
                    {block.points.map((pt) => (
                      <li key={pt} className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-checkbox-circle-fill text-[#4CAF50] text-lg" />
                        </div>
                        <span className="text-gray-600 text-[0.9375rem] leading-[1.75]">{pt}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="w-full lg:w-1/2 flex-shrink-0">
                  <div className="rounded-2xl overflow-hidden">
                    <img src={block.img} alt={block.title} className="w-full h-[320px] lg:h-[380px] object-cover object-top rounded-2xl" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ODM vs OEM Compare ── */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">サービスモデル比較</span>
            <h2 className="text-[#1a2e1a] font-bold text-[2rem] md:text-[2.5rem] mb-4">ODM vs OEM — あなたのブランドに最適なモデルを</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              両モデルにはそれぞれ優位性があります。ブランドの段階とニーズに基づいて最適な協業方式をお選びください
            </p>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-gray-100">
            <table className="w-full min-w-[560px]">
              <thead>
                <tr className="bg-[#f7faf7]">
                  <th className="text-left px-6 py-4 text-gray-400 text-[0.875rem] font-medium w-1/3">比較項目</th>
                  <th className="px-6 py-4 text-center w-1/3">
                    <span className="inline-block bg-[#4CAF50] text-white font-bold text-[1rem] px-5 py-1.5 rounded-lg">ODM</span>
                    <p className="text-gray-400 text-[0.75rem] mt-1">委託設計製造</p>
                  </th>
                  <th className="px-6 py-4 text-center w-1/3">
                    <span className="inline-block bg-[#2e7d32] text-white font-bold text-[1rem] px-5 py-1.5 rounded-lg">OEM</span>
                    <p className="text-gray-400 text-[0.75rem] mt-1">委託製造</p>
                  </th>
                </tr>
              </thead>
              <tbody>
                {compareRows.map((row, idx) => (
                  <tr key={row.feature} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#fafffe]'}>
                    <td className="px-6 py-4 text-[#1a2e1a] font-medium text-[0.9375rem] border-t border-gray-50">{row.feature}</td>
                    <td className="px-6 py-4 text-center text-gray-600 text-[0.9375rem] border-t border-gray-50">{row.odm}</td>
                    <td className="px-6 py-4 text-center text-gray-600 text-[0.9375rem] border-t border-gray-50">{row.oem}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/ja/contact/form" className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[1rem] px-8 py-4 rounded-full transition-all duration-300 cursor-pointer">
              最適なモデルを相談する <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/ja/partnership" className="whitespace-nowrap inline-flex items-center justify-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1rem] px-8 py-4 rounded-full transition-all duration-300 cursor-pointer">
              協業フローを見る
            </Link>
          </div>
        </div>
      </section>

      {/* ── Quality Section ── */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">品質管理</span>
            <h2 className="text-[#1a2e1a] font-bold text-[2rem] md:text-[2.5rem] mb-4">厳格な品質管理 — すべてのバッチを安全に</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              康永は包括的な品質管理システムを維持し、原料から完成品まで全工程を管理します
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
            {certifications.map((cert) => (
              <div key={cert.name} className="bg-[#f7faf7] rounded-2xl p-6 text-center border border-gray-100 hover:border-green-200 transition-all duration-300">
                <div className="w-14 h-14 flex items-center justify-center bg-green-50 rounded-xl mx-auto mb-4 border border-green-100">
                  <i className={`${cert.icon} text-[#4CAF50] text-2xl`} />
                </div>
                <h3 className="text-[#1a2e1a] font-bold text-[1rem] mb-1">{cert.name}</h3>
                <p className="text-gray-400 text-[0.8125rem]">{cert.desc}</p>
              </div>
            ))}
          </div>
          <div className="bg-[#f7faf7] rounded-2xl p-8 lg:p-10 border border-gray-100">
            <h3 className="text-[#1a2e1a] font-bold text-[1.25rem] mb-8 text-center">4段階品質管理フロー</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {qcSteps.map((step, idx) => (
                <div key={step.title} className="relative flex flex-col items-center text-center">
                  <div className="relative mb-4">
                    <div className="w-16 h-16 flex items-center justify-center bg-white rounded-full border-2 border-[#4CAF50]">
                      <i className={`${step.icon} text-[#4CAF50] text-2xl`} />
                    </div>
                    <span className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-[#4CAF50] text-white text-[0.6875rem] font-bold rounded-full">{idx + 1}</span>
                  </div>
                  <h4 className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-2">{step.title}</h4>
                  <p className="text-gray-500 text-[0.8125rem] leading-[1.75]">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Process Section ── */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">サービスフロー</span>
            <h2 className="text-[#1a2e1a] font-bold text-[2rem] md:text-[2.5rem] mb-4">6ステップ完全OEM製造フロー</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              明確で透明なサービスフロー — ご相談から納品まで、すべての段階を把握できます
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
            {processSteps.map((s) => (
              <div key={s.num} className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-green-200 hover:-translate-y-1 transition-all duration-300 flex gap-5">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 flex items-center justify-center bg-green-50 rounded-xl border border-green-100">
                    <i className={`${s.icon} text-[#4CAF50] text-xl`} />
                  </div>
                </div>
                <div>
                  <span className="text-[#4CAF50] font-bold text-[1.25rem] leading-none opacity-40">{s.num}</span>
                  <h3 className="text-[#1a2e1a] font-bold text-[1rem] mb-1.5 mt-0.5">{s.title}</h3>
                  <p className="text-gray-500 text-[0.875rem] leading-[1.75]">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link to="/ja/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
              協業を始める <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-14 lg:gap-20 items-start">
            <div className="w-full lg:w-1/3 flex-shrink-0">
              <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">よくある質問</span>
              <h2 className="text-[#1a2e1a] font-bold leading-tight mb-6 text-[1.75rem] md:text-[2rem]">OEM/ODMサービス<br />よくあるご質問</h2>
              <p className="text-gray-500 text-[1.0625rem] leading-[1.75] mb-8">
                その他ご質問がある場合は、営業チームに直接お問い合わせください。1営業日以内にご返答します。
              </p>
              <Link to="/ja/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[0.9375rem] px-6 py-3 rounded-full transition-all duration-300 cursor-pointer">
                お問い合わせ <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="w-full lg:w-2/3">
              <div className="space-y-3">
                {faqs.map((faq, idx) => (
                  <div key={idx} className={`bg-white rounded-xl border transition-all duration-200 overflow-hidden ${openFaqIdx === idx ? 'border-[#4CAF50]/40' : 'border-gray-100'}`}>
                    <button className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer" onClick={() => setOpenFaqIdx(openFaqIdx === idx ? null : idx)}>
                      <span className={`text-[1rem] font-semibold leading-snug pr-4 ${openFaqIdx === idx ? 'text-[#4CAF50]' : 'text-[#1a2e1a]'}`}>{faq.q}</span>
                      <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-200 ${openFaqIdx === idx ? 'bg-[#4CAF50] text-white' : 'bg-green-50 text-[#4CAF50]'}`}>
                        <i className={`text-sm ${openFaqIdx === idx ? 'ri-subtract-line' : 'ri-add-line'}`} />
                      </div>
                    </button>
                    {openFaqIdx === idx && (
                      <div className="px-6 pb-5">
                        <p className="text-gray-600 text-[0.9375rem] leading-[1.75]">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=top%20down%20aerial%20view%20of%20healthy%20natural%20ingredients%20herbs%20spices%20vegetables%20colorful%20arrangement%20on%20white%20background%2C%20health%20supplement%20raw%20materials%20flat%20lay%20photography&width=1920&height=600&seq=service-cta-bg-01&orientation=landscape"
            alt="OEM/ODM CTA"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-[#2d5a2d]/85" />
        </div>
        <div className="relative z-10 max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">OEM/ODM協業を始めましょう</span>
            <h2 className="text-white font-bold leading-tight mb-5 text-[2rem] md:text-[2.5rem]">康永を<br />あなたのOEM製造パートナーに</h2>
            <p className="text-green-200 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto mb-10">
              プロジェクトの規模にかかわらず、康永はプロフェッショナルで柔軟なOEM/ODMソリューションを提供します。今すぐお問い合わせいただき、製品をより早く市場に届けましょう。
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <Link to="/ja/contact/form" className="whitespace-nowrap bg-white text-[#2d5a2d] hover:bg-green-50 font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                OEM/ODMについてお問い合わせ
              </Link>
              <Link to="/ja/partnership" className="whitespace-nowrap bg-transparent border-2 border-white text-white hover:bg-white/10 font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                協業フローを見る
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { icon: 'ri-capsule-line', text: '多様な剤型' },
                { icon: 'ri-flask-line', text: '自社R&D研究室' },
                { icon: 'ri-award-line', text: 'GMP認定工場' },
                { icon: 'ri-customer-service-2-line', text: '専属担当者' },
              ].map((h) => (
                <div key={h.text} className="flex items-center justify-center gap-2 bg-white/10 rounded-xl py-4 px-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className={`${h.icon} text-green-300 text-xl`} />
                  </div>
                  <span className="text-white font-medium text-[0.9375rem] whitespace-nowrap">{h.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </JaLayout>
  );
}
